<script setup lang="ts">
import { computed } from 'vue'
import { useDonorPlan } from '@/composables/useDonorPlan'
import { useUpgradeModal } from '@/composables/useUpgradeModal'
import { useStatementsPage } from '@/composables/useStatementsPage'
import ApiErrorNotification from '@/components/generic/ApiErrorNotification.vue'
import DashboardDataLoading from '@/components/dashboard/ui/DashboardDataLoading.vue'
import StatementCurrentYearSection from '@/components/dashboard/statements/StatementCurrentYearSection.vue'
import StatementHistoricSection from '@/components/dashboard/statements/StatementHistoricSection.vue'

const { isPaid, isPlanKnown } = useDonorPlan()
const { openUpgradeModal } = useUpgradeModal()

const {
  latestIssuedStatement,
  latestStatementFinancialYear,
  historicStatementYears,
  hasHistoricYears,
  isPageLoading,
  apiError,
  hasContent,
  financialYearForPeriod,
} = useStatementsPage()

const showLatestStatementSection = computed(() =>
  Boolean(latestStatementFinancialYear.value)
)

const pageLoading = computed(() => !isPlanKnown.value || isPageLoading.value)

const onUpgrade = () => {
  openUpgradeModal()
}
</script>

<template>
  <div>
    <ApiErrorNotification
      v-if="apiError"
      link="Statements"
      aria-label="Statements API error"
    >
      An error has occurred while fetching statements, please
      <strong>try again</strong>
      .
    </ApiErrorNotification>

    <DashboardDataLoading v-else-if="pageLoading" />

    <div v-else-if="hasContent" class="sa-page-stack sa-stack">
      <section
        v-if="showLatestStatementSection"
        aria-labelledby="statements-latest-heading"
      >
        <div class="sa-section-hd">
          <h2 id="statements-latest-heading" class="sa-section-title">
            {{ $t('Dashboard.Statements.AnnualSummaryHeading') }}
          </h2>
        </div>

        <StatementCurrentYearSection
          :financial-year="latestStatementFinancialYear!"
          :issued-statement="latestIssuedStatement"
          :is-paid="isPaid"
          @upgrade="onUpgrade"
        />
      </section>

      <StatementHistoricSection
        v-if="hasHistoricYears"
        :years="historicStatementYears"
        :is-paid="isPaid"
        :financial-year-for-period="financialYearForPeriod"
        @upgrade="onUpgrade"
      />
    </div>

    <div v-else class="sa-statements-empty">
      <img
        src="@/assets/images/main/statement.svg"
        alt=""
        class="sa-statements-empty__img"
      />
      <h2 class="sa-statements-empty__title">
        {{ $t('Dashboard.Statements.NotTitle') }}
      </h2>
      <p class="sa-statements-empty__text">
        {{ $t('Dashboard.Statements.NotSub') }}
      </p>
    </div>
  </div>
</template>
